import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { Box, Button, Stack } from '@mui/material';

//styled
import {
  StyledCard,
  StyledCardContent,
  StyledTypographySubTitle,
  StyledTypographyTitle
} from '../../styled-components';
//constants
import {
  ISitePermission,
  IUser,
  UserRole,
  USERS_FIELDS,
  UsersAndPermissionFormLabels,
  UsersAndPermissionInputsForm,
  UsersErrorMessages
} from 'constants/user.constants';
import { pagesEnum } from 'constants/ui.constants';
//components
import CustomInputForm from '../../../components/InputsForm/CustomInput';
import { SitePermissionTable } from '../SitePermissionTable';
//icons
import { EmailIcon } from 'assets/icons/Email.icon';
import { PhoneIcon } from 'assets/icons/Phone.icon';
import { UserInfoIcon } from 'assets/icons/UserInfo.icon';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { createNewUser, getAllSites } from 'state/reducers/user.reducers';
import _ from 'lodash';
import { convertKeysToSnakeCase, formResolver, isPhoneNumberValid } from '../../../utils/global.util';
import { errorsMessages, setErrorMessage } from '../helper';
import { emailValidation } from 'components/AuthorizationForm/utils/passwordValidation';
import { updateSiteUsersCount } from 'state/reducers/site.reducers';
import NoInfo from 'components/NoInfo';

// Enum for new user types
export enum NewUserType {
  SUPER_ADMIN = 'new-super-admin',
  BWMR = 'new-BWMR',
  HCP = 'new-HCP'
}

interface IFormInputs {
  dataForm: UsersAndPermissionInputsForm;
  sitePermissions: ISitePermission[];
}

export const NewUser: React.FC = () => {
  const navigate = useNavigate();
  const { userType } = useParams<{ userType: NewUserType }>();
  const [formError, setFormError] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();
  const { sites, users } = useAppSelector((state) => state.user);

  useEffect(() => {
    dispatch(getAllSites());
  }, []);

  const titlePermission = {
    [NewUserType.BWMR]: {
      title: 'BWMR',
      role: UserRole.ADMIN
    },
    [NewUserType.HCP]: {
      title: 'HCP',
      role: UserRole.DOCTOR
    },
    [NewUserType.SUPER_ADMIN]: {
      title: 'Super Admin',
      role: UserRole.BW_ADMIN
    }
  };

  const setSelectedSitePermission = (): UserRole | undefined => {
    return _.has(titlePermission, String(userType)) ? _.get(titlePermission, userType || '').role : undefined;
  };

  const methods = useForm<IFormInputs>({
    reValidateMode: 'onChange',
    defaultValues: {
      dataForm: { phoneNumber: '' },
      sitePermissions: [{ id: undefined, role: setSelectedSitePermission() }]
    },
    mode: 'onTouched',
    resolver: (values) => formResolver(values, USERS_FIELDS)
  });

  const {
    formState: { errors }
  } = methods;

  // Watch the sitePermissions field to get the current values its necessary for disabling the save button
  const sitePermissions = methods.watch('sitePermissions');

  const formSubmitHandler: SubmitHandler<IFormInputs> = (newUserData: IFormInputs) => {
    newUserData.dataForm.email = newUserData?.dataForm?.email?.toLowerCase().trim();
    if (userType === NewUserType.SUPER_ADMIN) {
      const parsedNewUserData = {
        ...convertKeysToSnakeCase(newUserData.dataForm),
        is_super_admin: true
      };
      dispatch(createNewUser(parsedNewUserData)).then((res) => {
        if (_.has(res, 'error')) {
          setErrorMessage(res, setFormError, formError);
        } else {
          navigate(-1);
        }
      });
    } else {
      const parsedNewUserData = {
        ...convertKeysToSnakeCase(newUserData.dataForm),
        site_permissions: _.map(newUserData.sitePermissions, (sitePermission) => ({
          site_id: sitePermission.id,
          role: sitePermission.role
        })),
        userRole: titlePermission[userType as keyof typeof titlePermission].role ?? null
      };
      dispatch(createNewUser(parsedNewUserData)).then((res) => {
        if (_.has(res, 'error')) {
          setErrorMessage(res, setFormError, formError);
        } else {
          //update usersCount in sites reducer
          dispatch(
            updateSiteUsersCount({
              isNew: true,
              type: parsedNewUserData.userRole,
              sitesIds: _.map(newUserData.sitePermissions, 'id') as number[],
              isActive: true
            })
          );
          navigate(-1);
        }
      });
    }
  };

  const validateField = (event: any) => {
    const { id, value }: { id: keyof IUser; value: string } = event.target;
    if (id === 'phoneNumber') {
      const isValid = isPhoneNumberValid(value);
      if (!isValid) {
        setFormError({
          ...formError,
          [id]: UsersErrorMessages.INVALID_PHONE_NUMBER
        });
      } else {
        setFormError({
          ..._.omit(formError, id)
        });
      }
    }

    if (id === 'email' && !emailValidation(value)) {
      setFormError({
        ...formError,
        [id]: UsersErrorMessages.INVALID_EMAIL
      });
      return;
    }

    if (value?.length) {
      if (['email'].includes(id) && value) {
        const exist = !!users?.find((user: IUser) => {
          return user[id] === value;
        });
        if (exist) {
          setFormError({
            ...formError,
            [id]: _.get(errorsMessages, id) || ''
          });
          return;
        }
        setFormError({
          ..._.omit(formError, id)
        });
      }
    }
  };

  const isDisabled = () => {
    if (!methods.formState.isValid || Object.keys(formError).length !== 0) return true;
    const isHCP = userType === NewUserType.HCP;
    const isBWMR = userType === NewUserType.BWMR;
    if (isHCP || isBWMR) {
      // Disable the save button if any site is not selected or if there are no site permissions
      return sitePermissions.some((permission) => !permission.id) || sitePermissions.length === 0;
    }
    return false;
  };

  if (!userType || !_.has(titlePermission, userType)) return <NoInfo />;

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <FormProvider {...methods}>
        <form onChange={validateField} onSubmit={methods.handleSubmit(formSubmitHandler)}>
          <StyledCard sx={{ background: 'transparent' }}>
            <StyledCardContent>
              <Stack spacing={3}>
                <StyledTypographyTitle sx={{ justifyContent: 'flex-start' }}>
                  {userType === NewUserType.SUPER_ADMIN
                    ? 'Add New Super Admin'
                    : `Add New ${_.get(titlePermission, userType || '').title} User`}
                </StyledTypographyTitle>
                <StyledTypographySubTitle sx={{ justifyContent: 'flex-start' }}>
                  User Information
                </StyledTypographySubTitle>
                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.firstName'}
                    inputRegister={'dataForm.firstName'}
                    inputId={'firstName'}
                    label={UsersAndPermissionFormLabels.FirstName}
                    type={'text'}
                    icon={<UserInfoIcon />}
                    errorMessage={errors?.dataForm?.firstName?.message}
                  />
                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.lastName'}
                    inputRegister={'dataForm.lastName'}
                    inputId={'lastName'}
                    label={UsersAndPermissionFormLabels.LastName}
                    type={'text'}
                    icon={<UserInfoIcon />}
                    errorMessage={errors?.dataForm?.lastName?.message}
                  />
                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.phoneNumber'}
                    inputRegister={'dataForm.phoneNumber'}
                    inputId={'phoneNumber'}
                    label={UsersAndPermissionFormLabels.PhoneNumber}
                    type={'text'}
                    icon={<PhoneIcon />}
                    errorMessage={_.get(formError, 'phoneNumber') || ''}
                    isRequired={false}
                  />

                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.email'}
                    inputRegister={'dataForm.email'}
                    inputId={'email'}
                    label={UsersAndPermissionFormLabels.Email}
                    type={'text'}
                    icon={<EmailIcon />}
                    errorMessage={errors?.dataForm?.email?.message || _.get(formError, 'email') || ''}
                  />
                </Stack>
                {userType !== NewUserType.SUPER_ADMIN && (
                  <>
                    <StyledTypographySubTitle>Site Assignations</StyledTypographySubTitle>
                    <SitePermissionTable
                      role={titlePermission[userType as keyof typeof titlePermission].role}
                      isEditMode={false}
                      sitePermissions={sites}
                    />
                  </>
                )}
              </Stack>
              <Stack spacing={2} direction='row' sx={{ display: 'flex', justifyContent: 'flex-end', m: 10 }}>
                <Button
                  color='secondary'
                  data-testid='cancel-button'
                  size='large'
                  variant='text'
                  onClick={() => {
                    navigate(`/${pagesEnum.USERS_AND_PERMISSIONS}`);
                  }}
                >
                  cancel
                </Button>
                <Button
                  color='secondary'
                  variant='contained'
                  data-testid='save-button'
                  disabled={isDisabled()}
                  type='submit'
                  size='large'
                >
                  save
                </Button>
              </Stack>
            </StyledCardContent>
          </StyledCard>
        </form>
      </FormProvider>
    </Box>
  );
};
